<template>
	<transition name="full-video-fade">
		<div
			v-if="show"
			class="full-video"
			role="dialog"
			aria-modal="true"
			@click.self="close"
			@keydown.esc="close"
			tabindex="-1"
		>
			<div class="mask" @click="close"></div>
			<div class="preview-container" @click.stop>
				<div class="preview-frame">
					<div class="preview-toolbar">
						<div class="preview-meta">
							<div class="preview-title" :title="previewTitle">{{ previewTitle }}</div>
						</div>
						<div class="preview-toolbar-side">
							<div v-if="showStatusChip" class="video-status-chip" :class="statusChipClass">
								<span>{{ statusChipText }}</span>
							</div>
							<div class="toolbar-actions">
								<button
									type="button"
									class="toolbar-btn"
									:disabled="!source || isDownloading"
									:title="isDownloading ? '下载中' : '下载'"
									@click="downloadCurrent"
								>
									<i :class="isDownloading ? 'el-icon-loading' : 'el-icon-download'"></i>
								</button>
								<button class="toolbar-btn" type="button" @click="close" title="关闭">
									<i class="el-icon-close"></i>
								</button>
							</div>
						</div>
					</div>
					<div class="video-stage">
						<div class="video-shell" :class="{ 'is-loading': isLoading, 'has-error': hasError }">
							<img v-if="posterUrl && !url" class="video-poster" :src="posterUrl" alt="视频封面" />
							<div v-if="isLoading || hasError" class="viewer-placeholder">
								<div class="viewer-status-card">
									<div class="viewer-status-icon">
										<i :class="placeholderIcon"></i>
									</div>
									<div class="viewer-status-title">{{ placeholderTitle }}</div>
									<div class="viewer-status-text">{{ placeholderText }}</div>
									<button
										v-if="hasError"
										type="button"
										class="viewer-status-action"
										:disabled="isDownloading"
										@click="retryPreview"
									>
										{{ placeholderActionText }}
									</button>
								</div>
							</div>
							<video
								:key="requestId"
								:data-request-id="requestId"
								v-if="url"
								ref="player"
								class="video-player"
								:src="url"
								:poster="posterUrl || undefined"
								controls
								playsinline
								preload="metadata"
								controlslist="nodownload noplaybackrate"
								disablepictureinpicture
								@loadeddata="onVideoReady"
								@canplay="onVideoReady"
								@play="onVideoPlay"
								@pause="onVideoPause"
								@error="onVideoError"
							></video>
						</div>
					</div>
				</div>
			</div>
		</div>
	</transition>
</template>

<script>
export default {
	name: "fullVideo",
	data() {
		return {
			show: false,
			source: '',
			posterSource: '',
			strict: false,
			url: '',
			posterUrl: '',
			bodyOverflow: '',
			isLoading: false,
			hasError: false,
			errorText: '加载失败',
			isDownloading: false,
			isPlaying: false,
			requestId: 0,
			currentPayload: null,
			autoPlayPending: false
		};
	},
	mounted() {
		document.addEventListener('keydown', this.handleKeydown);
	},
	beforeDestroy() {
		document.removeEventListener('keydown', this.handleKeydown);
		this.resetPlayer();
		this.restoreBodyOverflow();
	},
	methods: {
		async open(source) {
			const videoPayload = typeof source === 'string' ? { url: source } : (source || {});
			const currentRequestId = ++this.requestId;
			this.currentPayload = { ...videoPayload };
			this.bodyOverflow = document.body.style.overflow;
			this.show = true;
			this.source = videoPayload.url || '';
			this.posterSource = videoPayload.poster || '';
			this.strict = Boolean(videoPayload.strict);
			this.url = '';
			this.posterUrl = '';
			this.isLoading = true;
			this.hasError = false;
			this.errorText = '加载失败';
			this.isDownloading = false;
			this.isPlaying = false;
			this.autoPlayPending = videoPayload.autoPlay === true;
			document.body.style.overflow = 'hidden';
			this.$nextTick(() => {
				if (this.$el && this.$el.focus) {
					this.$el.focus();
				}
			});
			if (!this.source) {
				this.handlePreviewError('视频暂不可用');
				return;
			}
			if (this.strict && !this.$assetResolver.hasAssetSource(this.source)) {
				this.handlePreviewError('视频暂不可用');
				return;
			}
			this.resolvePoster(currentRequestId);
			try {
				const resolvedUrl = await this.$assetResolver.resolveUrl(this.source, {
					kind: 'video',
					fileName: videoPayload.fileName
				});
				if (currentRequestId !== this.requestId) {
					return;
				}
				if (!resolvedUrl) {
					this.handlePreviewError('视频暂不可用');
					return;
				}
				this.url = resolvedUrl;
				this.$nextTick(() => {
					this.tryAutoPlay();
				});
			} catch (error) {
				if (currentRequestId !== this.requestId) {
					return;
				}
				this.handlePreviewError(error && error.message ? error.message : '视频加载失败');
			}
		},
		async resolvePoster(currentRequestId) {
			if (!this.posterSource) {
				return;
			}
			if (this.strict && !this.$assetResolver.hasAssetSource(this.posterSource)) {
				return;
			}
			try {
				const posterUrl = await this.$assetResolver.resolveUrl(this.posterSource, {
					kind: 'cover'
				});
				if (currentRequestId !== this.requestId || !posterUrl) {
					return;
				}
				this.posterUrl = posterUrl;
			} catch (error) {
				// 封面解析失败不阻断视频预览
			}
		},
		close() {
			this.requestId += 1;
			this.show = false;
			this.source = '';
			this.posterSource = '';
			this.strict = false;
			this.url = '';
			this.posterUrl = '';
			this.isLoading = false;
			this.hasError = false;
			this.errorText = '加载失败';
			this.isDownloading = false;
			this.isPlaying = false;
			this.currentPayload = null;
			this.autoPlayPending = false;
			this.resetPlayer();
			this.restoreBodyOverflow();
		},
		retryPreview() {
			if (!this.currentPayload) {
				return;
			}
			this.open({ ...this.currentPayload });
		},
		isCurrentMediaEvent(event) {
			const target = event && event.target;
			return this.show && !!target && target === this.$refs.player &&
				String(this.requestId) === target.dataset.requestId && target.getAttribute('src') === this.url;
		},
		onVideoReady(event) {
			if (!this.isCurrentMediaEvent(event)) return;
			this.isLoading = false;
			this.hasError = false;
			this.tryAutoPlay();
		},
		onVideoError(event) {
			if (!this.isCurrentMediaEvent(event)) return;
			this.handlePreviewError('视频加载失败');
		},
		onVideoPlay(event) {
			if (!this.isCurrentMediaEvent(event)) return;
			this.isPlaying = true;
			this.autoPlayPending = false;
		},
		onVideoPause(event) {
			if (!this.isCurrentMediaEvent(event)) return;
			this.isPlaying = false;
		},
		tryAutoPlay() {
			if (!this.autoPlayPending || !this.url) {
				return;
			}
			const player = this.$refs.player;
			if (!player) {
				return;
			}
			player.play().then(() => {
				this.autoPlayPending = false;
			}).catch(() => {
				this.autoPlayPending = false;
			});
		},
		handlePreviewError(message) {
			this.isLoading = false;
			this.hasError = true;
			this.errorText = message || '视频加载失败';
			this.autoPlayPending = false;
			this.resetPlayer();
		},
		handleKeydown(event) {
			if (event.key === 'Escape' && this.show) {
				this.close();
			}
		},
		resolvePreviewName() {
			if (this.currentPayload && this.currentPayload.fileName) {
				return this.currentPayload.fileName;
			}
			if (!this.source) {
				return '当前视频';
			}
			if (String(this.source).startsWith('data:')) {
				return '当前视频';
			}
			const normalized = String(this.source).split('?')[0].split('#')[0];
			const idx = normalized.lastIndexOf('/');
			if (idx < 0) {
				return normalized || '当前视频';
			}
			return normalized.slice(idx + 1) || '当前视频';
		},
		async downloadCurrent() {
			if (!this.source || this.isDownloading) {
				return;
			}
			this.isDownloading = true;
			try {
				await this.$assetResolver.download(this.source, {
					fileName: this.previewTitle
				});
				this.$message.success('视频下载已开始');
			} catch (error) {
				this.$message.error(error && error.message ? error.message : '视频下载失败');
			} finally {
				this.isDownloading = false;
			}
		},
		resetPlayer() {
			const player = this.$refs.player;
			if (!player) {
				return;
			}
			player.pause();
			player.removeAttribute('src');
			player.load();
		},
		restoreBodyOverflow() {
			document.body.style.overflow = this.bodyOverflow || '';
		}
	},
	computed: {
		previewTitle() {
			return this.resolvePreviewName();
		},
		placeholderTitle() {
			if (this.isLoading) {
				return '正在准备视频';
			}
			return '视频暂时无法打开';
		},
		placeholderText() {
			if (this.isLoading) {
				return '视频资源准备完成后会自动播放。';
			}
			return this.errorText || '视频加载失败，请稍后重试。';
		},
		placeholderIcon() {
			return this.isLoading ? 'el-icon-loading' : 'el-icon-video-camera';
		},
		placeholderActionText() {
			return this.isDownloading ? '处理中' : '重新加载';
		},
		showStatusChip() {
			return this.isDownloading || this.isLoading || this.hasError;
		},
		statusChipText() {
			if (this.isDownloading) {
				return '下载中';
			}
			if (this.isLoading) {
				return '加载中';
			}
			if (this.hasError) {
				return '加载失败';
			}
			return '';
		},
		statusChipClass() {
			if (this.isDownloading || this.isLoading) {
				return 'is-loading';
			}
			if (this.hasError) {
				return 'is-error';
			}
			return 'is-ready';
		}
	}
}
</script>

<style lang="scss">
.full-video {
	--preview-max-width: min(1240px, calc(100vw - 24px));
	--preview-max-height: min(92vh, calc(100vh - 24px));
	--preview-shell-padding: clamp(8px, 1.1vw, 12px);
	--preview-frame-radius: 16px;
	--preview-overlay-base: rgba(var(--im-black-rgb), 0.56);
	--preview-frame-bg: var(--im-surface-panel-strong);
	--preview-frame-border: var(--im-surface-panel-border);
	--preview-stage-bg: var(--im-background-secondary);
	--preview-shell-bg: var(--im-chat-bg);
	--preview-shadow: 0 12px 32px -18px rgba(var(--im-black-rgb), 0.18);
	position: fixed;
	inset: 0;
	z-index: 9999;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 12px;
	box-sizing: border-box;

	.mask {
		position: absolute;
		inset: 0;
		background: var(--preview-overlay-base);
	}

	.preview-container {
		position: relative;
		width: var(--preview-max-width);
		z-index: 1;
	}

	.preview-frame {
		position: relative;
		display: flex;
		flex-direction: column;
		width: 100%;
		max-height: var(--preview-max-height);
		border-radius: var(--preview-frame-radius);
		overflow: hidden;
		background: var(--preview-frame-bg);
		border: 1px solid var(--preview-frame-border);
		box-shadow: var(--preview-shadow);
	}

	.preview-toolbar {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 10px;
		padding: 12px 14px;
		background: var(--preview-frame-bg);
		border-bottom: 1px solid var(--preview-frame-border);
	}

	.preview-meta {
		min-width: 0;
		flex: 1;
	}

	.preview-title {
		font-size: clamp(14px, 1.1vw, 17px);
		font-weight: 600;
		line-height: 1.3;
		color: var(--im-text-color-primary);
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.preview-toolbar-side {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		flex-shrink: 0;
		min-width: 0;
	}

	.video-status-chip {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 6px;
		flex-shrink: 0;
		height: 28px;
		padding: 0 10px;
		border-radius: 999px;
		font-size: 11px;
		font-weight: 600;
		background: var(--im-control-bg);
		border: 1px solid var(--im-control-border);
		color: var(--im-text-color-light);

		&.is-loading {
			background: rgba(var(--im-primary-rgb), 0.06);
			border-color: rgba(var(--im-primary-rgb), 0.18);
			color: var(--im-color-primary);
		}

		&.is-error {
			background: rgba(var(--im-danger-rgb), 0.06);
			border-color: rgba(var(--im-danger-rgb), 0.18);
			color: var(--im-color-danger);
		}
	}

	.toolbar-actions {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		flex-shrink: 0;
	}

	.toolbar-btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 36px;
		height: 36px;
		padding: 0;
		border: 1px solid var(--im-control-border);
		border-radius: 10px;
		background: var(--im-control-bg);
		color: var(--im-icon-color-strong);
		cursor: pointer;
		transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;

		&:hover:not(:disabled) {
			background: var(--im-icon-hover-bg);
			border-color: var(--im-control-border-strong);
			color: var(--im-icon-hover-color);
		}

		&:disabled {
			cursor: not-allowed;
			opacity: 0.56;
		}

		i {
			font-size: 16px;
		}
	}

	.video-stage {
		min-height: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: var(--preview-shell-padding);
		background: var(--preview-stage-bg);
	}

	.video-shell {
		position: relative;
		width: min(100%, calc(var(--preview-max-width) - (var(--preview-shell-padding) * 2)));
		min-width: min(280px, calc(100vw - 40px));
		min-height: min(220px, calc(100vh - 220px));
		max-height: calc(var(--preview-max-height) - 88px);
		padding: 8px;
		border-radius: calc(var(--preview-frame-radius) - 2px);
		background: var(--preview-shell-bg);
		border: 1px solid var(--preview-frame-border);
		display: inline-flex;
		align-items: center;
		justify-content: center;
		overflow: hidden;
		box-sizing: border-box;

		&.is-loading,
		&.has-error {
			min-height: min(280px, calc(100vh - 180px));
		}
	}

	.video-player,
	.video-poster {
		display: block;
		width: auto;
		height: auto;
		max-width: calc(var(--preview-max-width) - (var(--preview-shell-padding) * 2) - 24px);
		max-height: calc(var(--preview-max-height) - 112px);
		border-radius: 10px;
		background: var(--im-chat-bg);
		border: 1px solid rgba(var(--im-black-rgb), 0.04);
		object-fit: contain;
	}

	.viewer-placeholder {
		position: absolute;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 20px;
		background: rgba(var(--im-background-rgb), 0.56);
		z-index: 2;
	}

	.viewer-status-card {
		width: min(320px, 100%);
		padding: 18px 16px;
		border-radius: 12px;
		border: 1px solid var(--im-control-border);
		background: var(--im-surface-panel);
		text-align: center;
	}

	.viewer-status-icon {
		width: 36px;
		height: 36px;
		margin: 0 auto 8px;
		border-radius: 999px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		background: rgba(var(--im-primary-rgb), 0.1);
		color: var(--im-color-primary);

		i {
			font-size: 16px;
		}
	}

	.viewer-status-title {
		font-size: 15px;
		font-weight: 600;
		line-height: 1.4;
		color: var(--im-text-color-primary);
	}

	.viewer-status-text {
		margin-top: 6px;
		font-size: 12px;
		line-height: 1.6;
		color: var(--im-text-color-light);
	}

	.viewer-status-action {
		min-width: 96px;
		height: 34px;
		margin-top: 14px;
		padding: 0 16px;
		border: 1px solid rgba(var(--im-primary-rgb), 0.18);
		border-radius: 10px;
		background: transparent;
		color: var(--im-color-primary);
		font-size: 12px;
		font-weight: 600;
		cursor: pointer;
		transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;

		&:hover:not(:disabled) {
			background: rgba(var(--im-primary-rgb), 0.12);
			border-color: rgba(var(--im-primary-rgb), 0.24);
		}

		&:disabled {
			cursor: not-allowed;
			opacity: 0.56;
		}
	}

	.video-shell.has-error {
		.viewer-status-icon {
			background: rgba(var(--im-danger-rgb), 0.1);
			color: var(--im-color-danger);
		}
	}
}

[data-theme="dark"] .full-video {
	--preview-overlay-base: rgba(var(--im-black-rgb), 0.72);
	--preview-frame-bg: var(--im-surface-panel-strong);
	--preview-frame-border: rgba(var(--im-white-rgb), 0.08);
	--preview-stage-bg: var(--im-background-secondary);
	--preview-shell-bg: var(--im-chat-bg-alt);
	--preview-shadow: 0 16px 40px -22px rgba(var(--im-black-rgb), 0.46);
}

.full-video-fade-enter-active,
.full-video-fade-leave-active {
	transition: opacity 0.24s ease;
}

.full-video-fade-enter-active .preview-frame,
.full-video-fade-leave-active .preview-frame {
	transition: transform 0.28s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.24s ease;
}

.full-video-fade-enter,
.full-video-fade-leave-to {
	opacity: 0;
}

.full-video-fade-enter .preview-frame,
.full-video-fade-leave-to .preview-frame {
	opacity: 0;
	transform: translateY(12px) scale(0.985);
}

@media (max-width: 640px) {
	.full-video {
		--preview-max-width: calc(100vw - 12px);
		--preview-max-height: calc(100vh - 12px);
		--preview-shell-padding: 8px;
		--preview-frame-radius: 14px;
		padding: 6px;

		.preview-container {
			width: var(--preview-max-width);
		}

		.preview-toolbar {
			padding: 10px 10px 8px;
			align-items: flex-start;
			flex-wrap: wrap;
			gap: 8px;
		}

		.preview-meta {
			width: 100%;
		}

		.preview-title {
			font-size: 14px;
		}

		.preview-toolbar-side {
			width: 100%;
			justify-content: space-between;
		}

		.toolbar-btn {
			width: 32px;
			height: 32px;
			border-radius: 9px;

			i {
				font-size: 14px;
			}
		}

		.video-shell {
			min-width: min(240px, calc(100vw - 24px));
			min-height: min(180px, calc(100vh - 180px));
		}
	}
}
</style>
